import React, { useState } from 'react';
import { Plus, Phone, Mail, Award, TrendingUp, Eye, X, MapPin, Calendar, BarChart3, Edit, Trash2, Search } from 'lucide-react';
import PurchaseForm from './PurchaseForm';

const Suppliers = ({
  suppliers: suppliersProp = [],
  products: productsProp = [],
  loading = false,
  onAddSupplier,
  onEditSupplier,
  onDeleteSupplier,
  onToggleStatus,
  totalProductsCount,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [weightFilter, setWeightFilter] = useState('all');
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [showPurchaseHistory, setShowPurchaseHistory] = useState(false);
  const [showPurchaseForm, setShowPurchaseForm] = useState(false);
  const [editingPurchase, setEditingPurchase] = useState(null);
  const [purchaseHistoryData, setPurchaseHistoryData] = useState(null);
  const [purchaseRecords, setPurchaseRecords] = useState([]);

  const suppliers = Array.isArray(suppliersProp) ? suppliersProp : [];

  const viewDetails = (supplier) => {
    setSelectedSupplier(supplier);
    setShowDetails(true);
  };

  const viewPurchaseHistory = (supplier) => {
    const supplierRecords = purchaseRecords.filter(record => record.supplierId === supplier.id);
    
    let historyData;
    if (supplierRecords.length > 0) {
      const monthlyData = {};
      supplierRecords.forEach(record => {
        const monthKey = record.month;
        if (!monthlyData[monthKey]) {
          monthlyData[monthKey] = { month: monthKey, amount: 0, due: 0, pending: 0, total: 0 };
        }
        monthlyData[monthKey].amount += record.amount;
        monthlyData[monthKey].due += record.due;
        monthlyData[monthKey].pending += record.pending;
        monthlyData[monthKey].total += record.total;
      });
      historyData = Object.values(monthlyData);
    } else {
      const months = ['2024-01', '2024-02', '2024-03', '2024-04', '2024-05', '2024-06'];
      historyData = months.map(month => {
        const amount = Math.floor(Math.random() * 50000) + 20000;
        const due = Math.floor(Math.random() * 15000) + 5000;
        const pending = Math.floor(Math.random() * 10000);
        const total = amount + due + pending;
        
        return { month, amount, due, pending, total };
      });
    }
    
    setSelectedSupplier(supplier);
    setPurchaseHistoryData(historyData);
    setShowPurchaseHistory(true);
  };

  const addPurchaseRecord = () => {
    setShowPurchaseForm(true);
  };

  const savePurchaseRecord = (purchaseData) => {
    if (editingPurchase) {
      setPurchaseRecords(prev => prev.map(record => 
        record.id === editingPurchase.id ? { ...purchaseData, id: editingPurchase.id } : record
      ));
      setEditingPurchase(null);
    } else {
      setPurchaseRecords(prev => [...prev, { ...purchaseData, id: Date.now() }]);
    }
  };

  const editPurchaseRecord = (record) => {
    setEditingPurchase(record);
    setShowPurchaseForm(true);
  };

  const deletePurchaseRecord = (recordId) => {
    if (window.confirm('Are you sure you want to delete this purchase record?')) {
      setPurchaseRecords(prev => prev.filter(record => record.id !== recordId));
      if (selectedSupplier) {
        viewPurchaseHistory(selectedSupplier);
      }
    }
  };

  const contactSupplier = (supplier) => {
    const message = `Hello ${supplier.name}, I am contacting you regarding our business partnership. Please let me know your availability for a discussion.`;
    const whatsappUrl = `https://wa.me/${supplier.contact.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const filteredSuppliers = suppliers.filter((s) => {
    const name = (s.name || '').toString().toLowerCase();
    const term = searchTerm.toLowerCase().trim();
    const matchesSearch = !term || name.includes(term) ||
      ((s.gstNumber || '').toString().toLowerCase().includes(term)) ||
      ((s.contact || s.phone || '').toString().includes(searchTerm));
    const matchesWeight = weightFilter === 'all' || (s.weightUnit || '').toString() === weightFilter;
    return matchesSearch && matchesWeight;
  });

  const totalProducts = totalProductsCount != null ? totalProductsCount : suppliers.reduce((sum, s) => sum + (Number(s.products) || 0), 0);

  return (
    <div className="ui-page animate-fade-in">
      <div className="mb-8">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-3xl lg:text-4xl font-extrabold text-slate-800 tracking-tight">
              Supplier Management
            </h1>
            <p className="text-slate-500 mt-1.5 font-medium">Coordinate and track details of organic produce partners.</p>
          </div>
          
          <button 
            onClick={() => onAddSupplier?.()}
            className="ui-btn-primary"
          >
            <Plus size={18} />
            <span>Add Supplier</span>
          </button>
        </div>
      </div>

      {/* Metrics Header */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div className="group">
          <div className="ui-card ui-card-pad bg-gradient-to-br from-white to-emerald-50/20 border-emerald-100/30 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 text-white shadow-md shadow-emerald-500/10">
                <Award size={20} />
              </div>
            </div>
            <h3 className="text-slate-500 text-[10px] font-bold uppercase tracking-wider mb-1">Total Suppliers</h3>
            <p className="text-2xl font-extrabold text-slate-800 tracking-tight">{loading ? '…' : suppliers.length}</p>
          </div>
        </div>
        <div className="group">
          <div className="ui-card ui-card-pad bg-gradient-to-br from-white to-blue-50/20 border-blue-100/30 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 text-white shadow-md shadow-blue-500/10">
                <TrendingUp size={20} />
              </div>
            </div>
            <h3 className="text-slate-500 text-[10px] font-bold uppercase tracking-wider mb-1">Total Catalog Products</h3>
            <p className="text-2xl font-extrabold text-slate-800 tracking-tight">{loading ? '…' : totalProducts}</p>
          </div>
        </div>
      </div>

      {/* Filter Card */}
      <div className="ui-card ui-card-pad mb-6 py-4">
        <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
          <div className="flex items-center gap-3 bg-slate-50 border border-slate-200/80 rounded-xl px-4 py-2 flex-1 w-full">
            <Search className="text-slate-400 shrink-0" size={18} />
            <input
              type="text"
              placeholder="Search by supplier name, contact, or GSTIN..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-1 outline-none text-slate-700 bg-transparent font-medium text-sm w-full"
            />
          </div>
          
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <span className="text-xs font-bold text-slate-400 uppercase tracking-wider whitespace-nowrap">Preferred Unit:</span>
            <select
              value={weightFilter}
              onChange={(e) => setWeightFilter(e.target.value)}
              className="ui-input py-2 font-semibold border-slate-200 text-xs w-full sm:w-auto"
            >
              <option value="all">All Units</option>
              <option value="Grams">Grams</option>
              <option value="Kilograms">Kilograms</option>
              <option value="Milliliters">Milliliters</option>
              <option value="Liters">Liters</option>
              <option value="Pieces">Pieces</option>
            </select>
          </div>
        </div>
      </div>

      {/* Supplier Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredSuppliers.map((supplier) => (
          <div
            key={supplier.id}
            className="ui-card hover:border-emerald-200/60 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-300 overflow-hidden flex flex-col group relative"
          >
            {/* Top color accent stripe */}
            <div className="h-1.5 w-full bg-gradient-to-r from-emerald-500 via-teal-500 to-green-600 shrink-0" />

            <div className="p-6 flex flex-col justify-between h-full">
              <div>
                <div className="flex items-start justify-between gap-3 mb-4">
                  <h3 className="font-extrabold text-slate-800 text-base tracking-tight leading-snug line-clamp-2 group-hover:text-emerald-600 transition-colors">
                    {supplier.name}
                  </h3>
                  <button
                    onClick={() => onToggleStatus?.(supplier.id)}
                    className={`shrink-0 px-2.5 py-0.5 rounded-full text-[9px] font-extrabold uppercase tracking-wider cursor-pointer border ${
                      (supplier.status || '').toString().toLowerCase() === 'active'
                        ? 'bg-emerald-50 text-emerald-700 border-emerald-100/60 hover:bg-emerald-100'
                        : 'bg-rose-50 text-rose-700 border-rose-100/60 hover:bg-rose-100'
                    }`}
                    title="Click to toggle status"
                  >
                    {supplier.status || 'inactive'}
                  </button>
                </div>

                {supplier.gstNumber && (
                  <p className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider mb-4 bg-slate-50 border border-slate-200/40 rounded px-2 py-0.5 w-fit">GSTIN: {supplier.gstNumber}</p>
                )}

                {/* Contact Points */}
                <div className="space-y-3 mb-6">
                  <div className="flex items-center gap-3">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-slate-50 border border-slate-200/50 text-slate-500">
                      <Phone size={13} />
                    </div>
                    <span className="text-xs text-slate-600 font-semibold truncate">{supplier.contact || supplier.phone || '—'}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-slate-50 border border-slate-200/50 text-slate-500">
                      <Mail size={13} />
                    </div>
                    <span className="text-xs text-slate-600 font-semibold truncate">{supplier.email || '—'}</span>
                  </div>
                </div>

                {supplier.description && (
                  <p className="text-xs text-slate-400 font-medium line-clamp-2 mb-6 leading-relaxed bg-slate-50/50 p-2.5 rounded-xl border border-slate-100">" {supplier.description} "</p>
                )}
              </div>

              {/* Action grid matrix */}
              <div className="grid grid-cols-2 gap-2 pt-4 border-t border-slate-100/80 shrink-0">
                <button
                  type="button"
                  onClick={() => viewDetails(supplier)}
                  className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-slate-200 bg-slate-50 hover:bg-slate-100 text-slate-700 py-2 text-xs font-bold uppercase tracking-wider cursor-pointer transition-colors shadow-sm"
                  title="View Profile Details"
                >
                  <Eye size={12} />
                  <span>Profile</span>
                </button>
                <button
                  type="button"
                  onClick={() => viewPurchaseHistory(supplier)}
                  className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-purple-100 bg-purple-50 hover:bg-purple-100 text-purple-700 py-2 text-xs font-bold uppercase tracking-wider cursor-pointer transition-colors shadow-sm"
                  title="Purchase History Log"
                >
                  <BarChart3 size={12} />
                  <span>History</span>
                </button>
                {onEditSupplier && (
                  <button
                    type="button"
                    onClick={() => onEditSupplier(supplier)}
                    className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-blue-100 bg-blue-50 hover:bg-blue-100 text-blue-700 py-2 text-xs font-bold uppercase tracking-wider cursor-pointer transition-colors shadow-sm"
                    title="Edit Information"
                  >
                    <Edit size={12} />
                    <span>Edit</span>
                  </button>
                )}
                {onDeleteSupplier && (
                  <button
                    type="button"
                    onClick={() => { if (window.confirm('Delete this supplier completely?')) onDeleteSupplier(supplier.id); }}
                    className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-rose-100 bg-rose-50 hover:bg-rose-100 text-rose-700 py-2 text-xs font-bold uppercase tracking-wider cursor-pointer transition-colors shadow-sm"
                    title="Delete Supplier"
                  >
                    <Trash2 size={12} />
                    <span>Delete</span>
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => contactSupplier(supplier)}
                  className="col-span-2 inline-flex items-center justify-center gap-1.5 rounded-xl border border-emerald-100 bg-emerald-50 hover:bg-emerald-600 hover:text-white text-emerald-700 py-2 text-xs font-bold uppercase tracking-wider cursor-pointer transition-all shadow-sm"
                  title="Message Supplier on WhatsApp"
                >
                  <Phone size={12} />
                  <span>Contact WhatsApp</span>
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredSuppliers.length === 0 && (
        <div className="ui-card p-12 text-center mt-6">
          <div className="w-16 h-16 bg-slate-50 border border-slate-200/60 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-inner shrink-0">
            <Award size={28} className="text-slate-400" />
          </div>
          <p className="text-slate-500 font-semibold text-sm">No suppliers matching your parameters were found.</p>
        </div>
      )}

      {/* Purchase History Modal */}
      {showPurchaseHistory && selectedSupplier && purchaseHistoryData && (
        <div className="ui-modal-overlay">
          <div className="bg-white rounded-[28px] border border-slate-100 shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col justify-between overflow-hidden animate-fade-in">
            <div className="p-6 border-b border-slate-100 flex justify-between items-center shrink-0">
              <div>
                <h3 className="text-base font-bold text-slate-800 tracking-tight">Purchase Invoices history</h3>
                <p className="text-xs text-slate-400 font-semibold mt-1">{selectedSupplier.name}</p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={addPurchaseRecord}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-xl flex items-center gap-1.5 font-bold text-xs uppercase tracking-wider shadow-sm shadow-emerald-500/10 cursor-pointer transition-colors"
                >
                  <Plus size={14} />
                  <span>New Purchase</span>
                </button>
                <button 
                  onClick={() => setShowPurchaseHistory(false)}
                  className="p-2 rounded-xl hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors cursor-pointer"
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            {/* Summary Metrics Cards */}
            <div className="p-6 pb-2 grid grid-cols-2 md:grid-cols-4 gap-4 shrink-0">
              {[
                { label: 'Purchased Total', val: purchaseHistoryData.reduce((sum, item) => sum + item.amount, 0), color: 'emerald', icon: TrendingUp },
                { label: 'Total Due',       val: purchaseHistoryData.reduce((sum, item) => sum + item.due, 0),    color: 'amber',   icon: Calendar },
                { label: 'Pending Out',     val: purchaseHistoryData.reduce((sum, item) => sum + item.pending, 0),color: 'rose',    icon: X },
                { label: 'Grand Aggregate', val: purchaseHistoryData.reduce((sum, item) => sum + item.total, 0),  color: 'blue',    icon: Award }
              ].map(card => {
                const Icon = card.icon;
                return (
                  <div key={card.label} className="bg-slate-50 border border-slate-200/50 rounded-2xl p-4">
                    <p className="text-slate-400 text-[10px] font-bold uppercase tracking-wider mb-1">{card.label}</p>
                    <p className="text-base font-extrabold text-slate-850">₹{card.val.toLocaleString()}</p>
                  </div>
                );
              })}
            </div>

            {/* Monthly Data Table */}
            <div className="p-6 overflow-y-auto flex-1 ui-scrollbar">
              <div className="ui-table-wrap">
                <table className="ui-table">
                  <thead>
                    <tr className="bg-slate-50/50">
                      <th className="ui-th">Month</th>
                      <th className="ui-th text-right">Paid Amount</th>
                      <th className="ui-th text-right">Due Amount</th>
                      <th className="ui-th text-right">Pending Amount</th>
                      <th className="ui-th text-right">Total Aggregate</th>
                      <th className="ui-th text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {purchaseRecords.filter(record => record.supplierId === selectedSupplier.id).map((record) => (
                      <tr key={record.id} className="hover:bg-slate-50/30 transition-colors">
                        <td className="ui-td font-extrabold text-slate-850">
                          {new Date(record.month + '-01').toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                        </td>
                        <td className="ui-td text-right font-bold text-emerald-600">
                          ₹{record.amount.toLocaleString()}
                        </td>
                        <td className="ui-td text-right font-bold text-amber-600">
                          ₹{record.due.toLocaleString()}
                        </td>
                        <td className="ui-td text-right font-bold text-rose-600">
                          ₹{record.pending.toLocaleString()}
                        </td>
                        <td className="ui-td text-right font-extrabold text-blue-600">
                          ₹{record.total.toLocaleString()}
                        </td>
                        <td className="ui-td text-center">
                          <div className="flex justify-center gap-1.5">
                            <button
                              onClick={() => editPurchaseRecord(record)}
                              className="bg-blue-50 hover:bg-blue-600 border border-blue-100 hover:border-blue-600 text-blue-600 hover:text-white p-2 rounded-xl cursor-pointer transition-colors shadow-sm"
                              title="Edit record"
                            >
                              <Edit size={12} />
                            </button>
                            <button
                              onClick={() => deletePurchaseRecord(record.id)}
                              className="bg-rose-50 hover:bg-rose-600 border border-rose-100 hover:border-rose-600 text-rose-600 hover:text-white p-2 rounded-xl cursor-pointer transition-colors shadow-sm"
                              title="Delete record"
                            >
                              <Trash2 size={12} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {purchaseRecords.filter(record => record.supplierId === selectedSupplier.id).length === 0 && (
                      <tr>
                        <td colSpan="6" className="ui-td text-center py-10 text-slate-400 font-semibold">
                          No purchase invoices logged. Click "New Purchase" to add details.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="p-6 border-t border-slate-100 bg-slate-50 flex justify-end shrink-0">
              <button
                onClick={() => setShowPurchaseHistory(false)}
                className="bg-white border border-slate-200 text-slate-700 py-2.5 px-6 rounded-xl hover:bg-slate-50 font-bold text-xs uppercase tracking-wider cursor-pointer shadow-sm transition-all duration-200"
              >
                Close History
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Purchase Form Modal */}
      {showPurchaseForm && selectedSupplier && (
        <PurchaseForm
          supplier={selectedSupplier}
          editData={editingPurchase}
          onClose={() => {
            setShowPurchaseForm(false);
            setEditingPurchase(null);
          }}
          onSave={savePurchaseRecord}
        />
      )}

      {/* Supplier Details Modal */}
      {showDetails && selectedSupplier && (
        <div className="ui-modal-overlay">
          <div className="bg-white rounded-[28px] border border-slate-100 shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto animate-fade-in flex flex-col justify-between">
            <div className="p-6 border-b border-slate-100 flex justify-between items-center">
              <h3 className="text-base font-bold text-slate-800 tracking-tight">Supplier Profile Details</h3>
              <button
                type="button"
                onClick={() => setShowDetails(false)}
                className="p-2 rounded-xl hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors cursor-pointer"
              >
                <X size={18} />
              </button>
            </div>

            <div className="p-6 space-y-4 flex-1 overflow-y-auto ui-scrollbar">
              <div className="bg-slate-50 border border-slate-200/50 p-5 rounded-2xl space-y-3">
                <p><span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Supplier Name</span><br /><span className="text-sm font-extrabold text-slate-800 mt-1 inline-block">{selectedSupplier.name || '—'}</span></p>
                <p>
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Trading Status</span><br />
                  <span className={`inline-block px-2.5 py-0.5 mt-1 rounded-full text-[9px] font-bold uppercase tracking-wider border ${(selectedSupplier.status || '').toString().toLowerCase() === 'active' ? 'bg-emerald-50 text-emerald-700 border-emerald-100/60' : 'bg-rose-50 text-rose-700 border-rose-100/60'}`}>
                    {selectedSupplier.status || 'inactive'}
                  </span>
                </p>
                {selectedSupplier.gstNumber != null && selectedSupplier.gstNumber !== '' && (
                  <p><span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">GSTIN Number</span><br /><span className="text-xs font-mono font-bold text-slate-700 mt-1 inline-block">{selectedSupplier.gstNumber}</span></p>
                )}
              </div>

              <div className="bg-slate-50 border border-slate-200/50 p-5 rounded-2xl space-y-3">
                <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-wider border-b border-slate-200/60 pb-1.5">Direct Contact Channels</h4>
                <div className="flex items-center gap-3">
                  <Phone size={14} className="text-slate-400 shrink-0" />
                  <span className="text-xs text-slate-700 font-bold">{selectedSupplier.contact || selectedSupplier.phone || '—'}</span>
                </div>
                <div className="flex items-center gap-3">
                  <Mail size={14} className="text-slate-400 shrink-0" />
                  <span className="text-xs text-slate-700 font-bold">{selectedSupplier.email || '—'}</span>
                </div>
                {(selectedSupplier.address != null && selectedSupplier.address !== '') && (
                  <div className="flex items-start gap-3">
                    <MapPin size={14} className="text-slate-400 shrink-0 mt-0.5" />
                    <span className="text-xs text-slate-600 font-medium leading-relaxed">{selectedSupplier.address}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="p-6 border-t border-slate-100 bg-slate-50 flex gap-3 shrink-0">
              <button
                type="button"
                onClick={() => contactSupplier(selectedSupplier)}
                className="flex-1 bg-gradient-to-r from-emerald-600 to-green-700 hover:from-emerald-700 hover:to-green-800 text-white py-3 rounded-xl font-bold text-xs uppercase tracking-wider flex items-center justify-center gap-1.5 cursor-pointer shadow-md shadow-emerald-500/10 hover:-translate-y-0.5 transition-all"
              >
                <Phone size={14} />
                <span>Contact Supplier</span>
              </button>
              <button
                type="button"
                onClick={() => setShowDetails(false)}
                className="flex-1 bg-white border border-slate-200 text-slate-700 py-3 rounded-xl hover:bg-slate-50 font-bold text-xs uppercase tracking-wider cursor-pointer shadow-sm transition-all duration-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Suppliers;